import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Rating,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight, FormatQuote } from '@mui/icons-material';
import { getTestimonials, getImageUrl } from '../../services/api';

const TestimonialCarousel = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await getTestimonials();
        setTestimonials(response.data.data);
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
      }
    };
    fetchTestimonials();
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  if (!testimonials.length) return null;

  const testimonial = testimonials[currentIndex];

  return (
    <Box sx={{ py: 8, bgcolor: 'primary.main' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          align="center"
          sx={{ color: 'white', mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' } }}
        >
          What Our Students Say
        </Typography>
        <Typography
          variant="h6"
          align="center"
          sx={{ color: 'rgba(255,255,255,0.7)', mb: 6 }}
        >
          Real stories from our successful students
        </Typography>

        <Box sx={{ position: 'relative', maxWidth: 800, mx: 'auto' }}>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              zIndex: 1,
            }}
          >
            <ChevronLeft />
          </IconButton>

          <Card sx={{ bgcolor: 'white', p: 4 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <FormatQuote
                sx={{ fontSize: 60, color: 'secondary.main', mb: 2, opacity: 0.5 }}
              />
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 3,
                  fontStyle: 'italic',
                }}
              >
                "{testimonial.reviewText}"
              </Typography>
              <Rating value={testimonial.rating} readOnly sx={{ mb: 2 }} />
              <Avatar
                src={getImageUrl(testimonial.profilePhoto)}
                sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {testimonial.studentName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {testimonial.designation}
              </Typography>
              {testimonial.collegeName && (
                <Typography variant="body2" sx={{ mt: 1, color: '#D4A853', fontWeight: 600 }}>
                  {testimonial.collegeName}
                </Typography>
              )}
            </CardContent>
          </Card>

          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              zIndex: 1,
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Dots */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
          {testimonials.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? '#D4A853' : 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default TestimonialCarousel;